import httpClient from './httpClient';
import {
  PatientPrivacySetting,
  UpdateUserFormDto,
  UserDto,
  UserInfoDto,
} from '../types/backendType';
import axios from 'axios';

//Get /account-mgmt/users/{user_id}
export async function fetchUser(userId: string): Promise<UserDto | undefined> {
  try {
    const response = await httpClient.get<UserDto>(`/account-mgmt/users/${userId}`);
    const user = response.data;
    if (user.user_id !== userId) {
      throw new Error(`Failed to fetch user ${userId}: incompatible ids`);
    }
    return user;
  } catch (e) {
    throw new Error(`Failed to fetch user ${userId}: ${e}`);
  }
}

function buildFetchUsersParams({ emrUserId }: { emrUserId?: string }) {
  let params: { emr_user_id?: string } = {};
  if (emrUserId) {
    params = { emr_user_id: emrUserId };
  }
  return params;
}

//GET /account-mgmt/users
export async function fetchUsers({ emrUserId }: { emrUserId?: string }): Promise<UserDto[]> {
  try {
    const response = await httpClient.get<UserDto[]>('/account-mgmt/users', {
      params: buildFetchUsersParams({ emrUserId }),
    });
    const users = response.data;
    return users;
  } catch (e) {
    throw new Error(`Failed to fetch users: ${e}`);
  }
}

// The backend signals a duplicate email in two different body shapes:
// APIException 409s (e.g. AddUserAPI's "User already exists") are sent as a
// raw string body, while other failures use { details: "..." }. Coerce both
// to a string and match case-insensitively. The previous version only looked
// at `data.details` for the literal "email already exists" — it missed the
// raw-string "User already exists" body AND threw a TypeError from the `in`
// operator when `data` was a string, which surfaced the generic error message.
function handleUserCreationErrors(e: unknown) {
  if (!axios.isAxiosError(e)) {
    return;
  }
  const data = e.response?.data;
  const message =
    typeof data === 'string' ? data : typeof data?.details === 'string' ? data.details : '';
  if (/already exist/i.test(message)) {
    throw new Error('email already exists');
  }
}

//POST /account-mgmt/users
export async function createUser({
  userInfo,
  assignorUserId,
}: {
  userInfo: UserInfoDto;
  assignorUserId: string;
}): Promise<UserDto> {
  try {
    const response = await httpClient.post<UserDto>('/account-mgmt/users', userInfo, {
      headers: { 'x-user-id': assignorUserId },
      params: {
        test_user: userInfo.additional_user_info?.is_test_user,
      },
    });
    const user = response.data;
    return user;
  } catch (e) {
    handleUserCreationErrors(e);
    throw new Error(`Failed to create user: ${e}`);
  }
}

//patch /account-mgmt/users/{user_id}
export async function updateUser({
  updateUserFormDto,
  userId,
}: {
  updateUserFormDto: UpdateUserFormDto;
  userId: string;
}): Promise<UserDto> {
  try {
    const response = await httpClient.patch<UserDto>(
      `/account-mgmt/users/${userId}`,
      updateUserFormDto,
    );
    const user = response.data;
    return user;
  } catch (e) {
    handleUserCreationErrors(e);
    throw new Error(`Failed to update user: ${e}`);
  }
}

// Admin-only sub-actions. Backend gates these via OPA (account-mgmt-clinician.rego).
// These do NOT wrap their errors with `new Error(...)` because thunks need to inspect
// `error.response.data` to detect the cross-region 409
// (body: { error: "cross_region_action", userRegion: "<region>" }).

// POST /account-mgmt/users/{user-id}/reset-password
export async function resetUserPassword(userId: string): Promise<void> {
  await httpClient.post(`/account-mgmt/users/${userId}/reset-password`);
}

// POST /account-mgmt/users/{user-id}/resend-invite
export async function resendUserInvite(userId: string): Promise<void> {
  await httpClient.post(`/account-mgmt/users/${userId}/resend-invite`);
}

// PATCH /account-mgmt/users/{user-id}/role
export async function changeUserRole(
  userId: string,
  role: 'admin' | 'clinician',
): Promise<UserDto> {
  const response = await httpClient.patch<UserDto>(`/account-mgmt/users/${userId}/role`, {
    role,
  });
  return response.data;
}

// PATCH /account-mgmt/users/{user-id}/email
export async function changeUserEmail(userId: string, email: string): Promise<UserDto> {
  const response = await httpClient.patch<UserDto>(`/account-mgmt/users/${userId}/email`, {
    email,
  });
  return response.data;
}

// POST /account-mgmt/users/{user-id}/archive
export async function archiveUser(userId: string): Promise<UserDto> {
  const response = await httpClient.post<UserDto>(`/account-mgmt/users/${userId}/archive`);
  return response.data;
}

// POST /account-mgmt/users/{user-id}/unarchive
export async function unarchiveUser(userId: string): Promise<UserDto> {
  const response = await httpClient.post<UserDto>(`/account-mgmt/users/${userId}/unarchive`);
  return response.data;
}

// Returns a user-friendly message for an admin action error. Specifically detects
// the cross-region 409 and embeds the region name; otherwise falls through to a
// generic message. Defensive about Axios's auto-parsing — `e.response.data` may be
// a string OR an already-parsed object depending on the response Content-Type.
export function adminActionErrorMessage(e: unknown, fallback: string): string {
  if (!axios.isAxiosError(e)) {
    return fallback;
  }
  const status = e.response?.status;
  const raw = e.response?.data;

  // Try to coerce the body into an object regardless of how Axios delivered it.
  let parsed: any = undefined;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // not JSON — keep `parsed` undefined and let the fallthrough handle it
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw;
  }

  if (status === 409 && parsed?.error === 'cross_region_action' && parsed?.userRegion) {
    return (
      `This user is registered in ${parsed.userRegion}. ` +
      'Use the admin portal in that region to manage them.'
    );
  }

  // 409 from Cognito (e.g. resend-invite already-confirmed) returns a plain string body.
  if (status === 409 && typeof raw === 'string' && raw) {
    return raw;
  }

  return fallback;
}

//get /account-mgmt/users/consent
export async function fetchUserPrivacyConsents(
  userId: string,
  filter?: { document_id: string; document_version?: string },
) {
  try {
    const response = await httpClient.get('/account-mgmt/users/consent', {
      headers: { 'x-user-id': userId },
      params: filter,
    });
    const userConsents = response.data;
    return userConsents;
  } catch (e) {
    throw new Error(`Failed to fetch user privacy settings: ${e}`);
  }
}

//post /account-mgmt/users/consent
export async function updateUserPrivacyConsents(
  userId: string,
  privacySetting: PatientPrivacySetting,
) {
  try {
    await httpClient.post('/account-mgmt/users/consent', privacySetting, {
      headers: { 'x-user-id': userId },
    });
  } catch (e) {
    throw new Error(`Failed to update user privacy settings: ${e}`);
  }
}
